import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Leaf, Sun, Snowflake, Sparkles, X } from "lucide-react";

const SEASONS = {
  spring: { icon: Leaf, label: "Spring", title: "Spring garden tidy-ups", text: "Get your lawn, borders and fences ready for the warmer months.", service: "garden" },
  summer: { icon: Sun, label: "Summer", title: "Summer lawns & driveways", text: "Regular grass cutting and driveway pressure washing to keep things looking sharp.", service: "garden" },
  autumn: { icon: Sparkles, label: "Autumn", title: "Autumn deep cleans", text: "Freshen up your home and carpets before the cosy season sets in.", service: "cleaning" },
  winter: { icon: Snowflake, label: "Winter", title: "Winter indoor refresh", text: "Painting, decorating and insulation – a great time to improve your home inside.", service: "interior" },
};

const seasonFor = (m) => (m >= 2 && m <= 4 ? "spring" : m >= 5 && m <= 7 ? "summer" : m >= 8 && m <= 10 ? "autumn" : "winter");

export const SeasonalBanner = () => {
  const [open, setOpen] = useState(() => sessionStorage.getItem("seasonal-banner-hidden") !== "1");
  const s = SEASONS[seasonFor(new Date().getMonth())];
  const Icon = s.icon;
  const close = () => { sessionStorage.setItem("seasonal-banner-hidden", "1"); setOpen(false); };

  return (
    <AnimatePresence>
      {open && (
        <motion.section initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden bg-[var(--green-tint)]" data-testid="seasonal-banner" aria-label={`${s.label} services`}>
          <div className="mx-auto max-w-7xl px-6 lg:px-10 py-5 flex items-center gap-4 sm:gap-6">
            <motion.span initial={{ rotate: -20, scale: 0.6 }} animate={{ rotate: 0, scale: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 200, damping: 14 }}
              className="hidden sm:grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[var(--green)] text-white">
              <Icon className="h-5 w-5" />
            </motion.span>
            <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-center gap-3 md:gap-8">
              <div className="flex-1">
                <p className="eyebrow text-[var(--green)]">This {s.label.toLowerCase()}</p>
                <p className="mt-1 font-display text-lg sm:text-xl font-semibold text-[var(--navy)]" data-testid="seasonal-banner-title">{s.title}</p>
                <p className="text-sm text-slate-700">{s.text}</p>
              </div>
              <Link to={`/quote?service=${s.service}`} data-testid="seasonal-banner-cta"
                className="group inline-flex w-fit items-center gap-2 rounded-full bg-[var(--navy)] px-5 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5">
                Get a free quote <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
              </Link>
            </div>
            <button type="button" onClick={close} data-testid="seasonal-banner-close" aria-label="Hide seasonal banner"
              className="self-start grid h-9 w-9 shrink-0 place-items-center rounded-full text-[var(--navy)] hover:bg-white/70 transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  );
};
